import { Address, ManualAddressSchemaType } from './address'
import { AddressSchemaType } from 'directFlow/pages/Drivers/schema/addressSchema'

import { IsoDateFormat, MedicalCondition, PreviousInsuranceCancellationReasons } from 'types'

export interface Name {
  title: string
  firstName: string
  lastName: string
}

export interface UserInfo {
  firstName: Name['firstName']
  lastName: Name['lastName']
  dateOfBirth: string
  phone: PolicyHolderQuoteV2['phoneNumber']
  email: PolicyHolderQuoteV2['email']
  occupation: NonNullable<PolicyHolderQuoteV2['employment']['primary']>['occupation']
  industry: NonNullable<PolicyHolderQuoteV2['employment']['primary']>['industry']
  currentAddress: Address['label']
  yearsInUK: PolicyHolderQuoteV2['yearsInUk']
  yearsNoClaims: number | undefined
  claimsOrConvictions: boolean
}

export enum LicenceType {
  UK = 'UK',
  UK_AUTO = 'UK_AUTO',
  UK_FULL = 'UK_FULL',
  UK_PROVISIONAL = 'UK_PROVISIONAL',
  EUROPEAN = 'EUROPEAN',
  OTHER_EUROPEAN = 'OTHER_EUROPEAN',
  INTERNATIONAL = 'INTERNATIONAL',
}

export type LicenceDuration = {
  unit: 'YEARS'
  length: number
}

export type LicenceDurationMonthAndYear = {
  year: number | null
  month: number | null
}

export type LicenceDataCompleteSchema = {
  countryOfFirstTest: string | null
  duration: LicenceDurationMonthAndYear | LicenceDuration | null
  ukDuration: LicenceDurationMonthAndYear | LicenceDuration | null
  number: string | null
  hasAnotherLicence?: boolean
  type: LicenceType
}

export interface LicenceDataQuoteV2 extends LicenceDataCompleteSchema {
  type: LicenceType
  provisional: boolean
}

type Claim = {
  year: number
  month: string
  type: string
  atFault: boolean
}

type Conviction = {
  year: number
  month: string
  code: string
}

type Employment = {
  status: string | null
  primary: {
    occupation: string
    industry: string
  } | null
  secondary: {
    occupation: string | null
    industry: string | null
  } | null
}

type Student = {
  university: string
  course: string
  drivenDuringTerm: boolean
  universityEmail: string | null
}

type NCD = {
  ukYears: number
  internationalYears: number | null
  otherVehicleYears: number | null
}

interface PolicyHolderBase {
  name: {
    title: string
    firstName: string
    lastName: string
  }
  email: string
  phoneNumber: string | null
  dateOfBirth: IsoDateFormat
  mainDriver: boolean
  previousInsuranceCancelled: boolean | null
  previousInsuranceCancelledInfo: {
    reason: PreviousInsuranceCancellationReasons
    cancellationYear?: string
  } | null
  employment: Employment
  monthsInUk: number | null
  bornInUk: boolean | null
  homeOwner: boolean
  drivesOtherCars: boolean
  useOfOtherVehicle: string | null
  nonMotoringConvictions: boolean | null
  claims: Claim[]
  convictions: Conviction[]
  student: Student | null
  ncd: NCD
  yearsInUk: number | null
  totalCarsInHousehold: number | null
  medicalCondition: MedicalCondition
}

export interface PolicyHolderQuoteV2 extends PolicyHolderBase {
  id: string
  currentAddress: AddressSchemaType
  licence: LicenceDataQuoteV2
}

export interface PolicyHolderCompleteSchema extends PolicyHolderBase {
  currentAddress: AddressSchemaType | ManualAddressSchemaType
  // We no longer consider previousAddress a pricing factor
  previousAddress: null
  licence: LicenceDataCompleteSchema
}
